// District tenancy (Phase 3g): school context in URLs for multi-site users.
// See Developer/DISTRICT_TENANCY_DESIGN.md §9.

export const SCHOOL_QUERY_PARAM = 'school'

/**
 * Read the ?school= query param from a vue-router query object.
 * @param {Record<string, unknown>|undefined} query
 * @returns {string|null}
 */
export function parseSchoolQueryParam(query) {
  const raw = query?.[SCHOOL_QUERY_PARAM]
  if (typeof raw !== 'string' || !raw.trim()) return null
  return raw.trim()
}

/**
 * Build an absolute app URL with optional ?school= for shareable links (email).
 * @param {string} origin  e.g. window.location.origin
 * @param {string} path    e.g. /feedback/abc or /students
 * @param {string|null|undefined} schoolId
 */
export function buildAppUrl(origin, path, schoolId) {
  const normalizedOrigin = (origin || '').replace(/\/$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const base = `${normalizedOrigin}${normalizedPath}`
  if (!schoolId) return base
  const sep = base.includes('?') ? '&' : '?'
  return `${base}${sep}${SCHOOL_QUERY_PARAM}=${encodeURIComponent(schoolId)}`
}

/** Feedback form deep link — always include school when known. */
export function buildFeedbackFormUrl(origin, instanceId, schoolId) {
  return buildAppUrl(origin, `/feedback/${instanceId}`, schoolId)
}

/**
 * Vue-router location with ?school= set (or removed when schoolId is empty).
 * @param {import('vue-router').RouteLocationNormalized} route
 * @param {string|null|undefined} schoolId
 */
export function routeLocationWithSchool(route, schoolId) {
  const query = { ...route.query }
  if (schoolId) {
    query[SCHOOL_QUERY_PARAM] = schoolId
  } else {
    delete query[SCHOOL_QUERY_PARAM]
  }
  return {
    path: route.path,
    hash: route.hash,
    query,
    params: route.params
  }
}

/**
 * Build a vue-router location for a path, stamping ?school= when known.
 * Hamburger (and other in-app) navigations should use this so the school-sync
 * guard does not have to bounce the first click through a redirect.
 * @param {string} path
 * @param {string|null|undefined} schoolId
 * @param {Record<string, unknown>} [query]
 */
export function pathWithSchool(path, schoolId, query = {}) {
  const nextQuery = { ...query }
  if (schoolId) {
    nextQuery[SCHOOL_QUERY_PARAM] = schoolId
  } else {
    delete nextQuery[SCHOOL_QUERY_PARAM]
  }
  return Object.keys(nextQuery).length ? { path, query: nextQuery } : { path }
}

/** Redirect target for /students preserving active school when multi-site. */
export function studentsRouteLocation(schoolId) {
  return pathWithSchool('/students', schoolId)
}

/** Admin → App Settings page (school, classes, meetings, feedback, appearance). */
export function adminAppSettingsLocation(schoolId, section = 'feedback') {
  const tab = ['school', 'classes', 'meetings', 'feedback', 'appearance'].includes(section)
    ? `settings-${section}`
    : 'settings-school'
  const query = { tab, category: 'app-settings' }
  if (schoolId) query[SCHOOL_QUERY_PARAM] = schoolId
  return { path: '/admin', query }
}

/**
 * Query param carrying the intended destination through the login flow, so a
 * deep link (e.g. a feedback form) survives a sign-in round-trip.
 */
export const REDIRECT_QUERY_PARAM = 'redirect'

/**
 * sessionStorage key used to preserve the post-login destination across the
 * mobile full-page Google redirect (signInWithRedirect reloads the app).
 */
export const REDIRECT_STORAGE_KEY = 'cmv_post_login_redirect'

/**
 * Validate a post-login redirect target. Only same-origin app paths are
 * allowed (must start with a single "/") to prevent open-redirect attacks via
 * a crafted ?redirect= value. Also refuses to bounce back to /login.
 * @param {unknown} redirect
 * @returns {string|null} the safe path, or null when unusable
 */
export function getSafeRedirectPath(redirect) {
  if (typeof redirect !== 'string' || !redirect) return null
  // Reject protocol-relative ("//host") and absolute URLs ("http://…").
  if (!redirect.startsWith('/') || redirect.startsWith('//')) return null
  if (redirect.startsWith('/login')) return null
  return redirect
}

/**
 * Vue Router 4 does not parse query/hash out of `{ path: '/foo?bar=1' }`.
 * Split a safe app path (optionally with search/hash) into a location object.
 * @param {unknown} redirect
 * @returns {{ path: string, query?: Record<string, string|string[]>, hash?: string }|null}
 */
export function appPathToLocation(redirect) {
  const safe = getSafeRedirectPath(redirect)
  if (!safe) return null

  const hashIndex = safe.indexOf('#')
  const hash = hashIndex >= 0 ? safe.slice(hashIndex) : ''
  const withoutHash = hashIndex >= 0 ? safe.slice(0, hashIndex) : safe
  const qIndex = withoutHash.indexOf('?')
  const path = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash
  const search = qIndex >= 0 ? withoutHash.slice(qIndex + 1) : ''

  const query = {}
  if (search) {
    const params = new URLSearchParams(search)
    params.forEach((value, key) => {
      if (Object.prototype.hasOwnProperty.call(query, key)) {
        const prev = query[key]
        query[key] = Array.isArray(prev) ? [...prev, value] : [prev, value]
      } else {
        query[key] = value
      }
    })
  }

  const loc = { path }
  if (Object.keys(query).length) loc.query = query
  if (hash) loc.hash = hash
  return loc
}

/** Persist a safe post-login destination across a Google redirect reload. */
export function stashPostLoginRedirect(redirect) {
  const dest = getSafeRedirectPath(redirect)
  if (!dest) return
  try {
    sessionStorage.setItem(REDIRECT_STORAGE_KEY, dest)
  } catch {
    /* sessionStorage unavailable */
  }
}

/** Read and clear the stashed post-login destination. */
export function takePostLoginRedirect() {
  try {
    const dest = getSafeRedirectPath(sessionStorage.getItem(REDIRECT_STORAGE_KEY))
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
    return dest
  } catch {
    return null
  }
}

/**
 * /login URL that remembers the current page (or a given app path) so sign-in
 * returns the user to a deep link instead of the default home page.
 * @param {string} [pathWithSearchAndHash]
 */
export function loginHrefForPath(pathWithSearchAndHash) {
  const dest = getSafeRedirectPath(pathWithSearchAndHash)
  if (!dest) return '/login'
  return `/login?${REDIRECT_QUERY_PARAM}=${encodeURIComponent(dest)}`
}

/** Same as loginHrefForPath, using window.location when in the browser. */
export function loginHrefForCurrentLocation() {
  if (typeof window === 'undefined') return '/login'
  return loginHrefForPath(window.location.pathname + window.location.search + window.location.hash)
}
